import { useState, useEffect, useRef } from 'react';
import RepMascot from '../components/RepMascot';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getCurriculum, getVideoLibrary, getTrainingVideoUrl, logTrainingActivity, getMyTests } from '../utils/api';
import MyTests from '../components/MyTests';
import toast from 'react-hot-toast';

// Slides are shown inside each lesson, not on the step list. Set true to restore.
const SHOW_STEP_SLIDES = false;

/* Reference material shipped in public/presentations — no course, no quiz.
   Titled by the question a rep asks, not by what the file is called. */
const GUIDES = [
  { id: 'inspection', icon: '🔎', title: 'What am I looking at?', sub: '79 problems you might find in a home',
    tag: 'Search', url: '/presentations/cpr-inspection-library/index.html' },
  { id: 'glossary', icon: '📖', title: 'What does this word mean?', sub: '133 roofing terms in plain English',
    tag: 'Search', url: '/presentations/cpr-roofing-glossary/index.html' },
  { id: 'roof-basics', icon: '🏠', title: 'How a roof works', sub: 'Parts, materials, and how to measure one',
    tag: '15 min', url: '/presentations/cpr-roof-basic-training/index.html' },
  { id: 'owens-corning', icon: '🛡️', title: 'Why our roof is better', sub: 'The Owens Corning system explained',
    tag: '20 min', url: '/presentations/cpr-oc-roofing-system/index.html' },
  { id: 'appointment', icon: '🚪', title: 'How to run an appointment', sub: 'Door to close, step by step',
    tag: '20 min', url: '/presentations/cpr-full-demonstration/index.html' },
];


/* Slides that belong to each step, from both vibrant decks.
   Matched on a keyword in the step title so renaming a step won't break it.
   ROOF = /presentations/cpr-roofing-vibrant, COAT = /presentations/cpr-coating-vibrant */
const ROOF = 'roof';
const COAT = 'coat';
const SLIDE_IMG = (deck, n) => `/step-slides/${deck}-${String(n).padStart(2, '0')}.jpg`;
const STEP_SLIDES = [
  { match: 'intro',      slides: [
      { deck: COAT, n: 1,  label: 'Opening slide' },
      { deck: ROOF, n: 1,  label: 'Roofing opener' } ] },
  { match: 'inspection', slides: [
      { deck: ROOF, n: 7,  label: 'Your roof is what separates you from the elements' },
      { deck: ROOF, n: 8,  label: 'The damage — what to point out' } ] },
  { match: 'set the stage', slides: [
      { deck: COAT, n: 1,  label: 'Opening slide' } ] },
  { match: 'company',    slides: [
      { deck: COAT, n: 2,  label: 'Finding a dependable company isn\u2019t easy' },
      { deck: COAT, n: 3,  label: 'Q1 — How much experience do you have?' },
      { deck: COAT, n: 5,  label: 'Q2 — Who is assigned to my project?' },
      { deck: COAT, n: 6,  label: 'Q3 — Repeat and referral business' },
      { deck: COAT, n: 9,  label: 'Excellent track record' },
      { deck: ROOF, n: 3,  label: 'Licensed where you live — 163 cities' },
      { deck: ROOF, n: 4,  label: 'Track record — CSLB, BBB, Edison' } ] },
  { match: 'product demo', slides: [
      { deck: COAT, n: 10, label: 'What options do you have?' },
      { deck: COAT, n: 11, label: 'Option 1 — Do nothing' },
      { deck: COAT, n: 12, label: 'Option 2 — Paint' },
      { deck: COAT, n: 13, label: 'Option 3 — Re-stucco' },
      { deck: COAT, n: 14, label: 'Option 4 — Color coat' },
      { deck: COAT, n: 15, label: 'Option 5 — Vinyl siding' },
      { deck: COAT, n: 16, label: 'The ultimate option — COOLWALL®' },
      { deck: COAT, n: 19, label: 'Step 1 — Trenching' },
      { deck: COAT, n: 20, label: 'Step 2 — Hydro-blast' },
      { deck: COAT, n: 23, label: 'Step 4 — Recondition & repair' },
      { deck: COAT, n: 25, label: 'Step 6 — Masking' },
      { deck: COAT, n: 27, label: 'Step 7 — Primer' },
      { deck: ROOF, n: 10, label: 'Roofing — permits & preparation' },
      { deck: ROOF, n: 19, label: 'Roofing — underlayment' },
      { deck: ROOF, n: 21, label: 'Roofing — why Owens Corning' } ] },
  { match: 'pricing',    slides: [
      { deck: COAT, n: 30, label: 'What does the price include?' },
      { deck: ROOF, n: 30, label: 'What does the price include?' },
      { deck: ROOF, n: 31, label: 'All-inclusive price policy' },
      { deck: ROOF, n: 32, label: 'Financing options' } ] },
  { match: "today", slides: [
      { deck: COAT, n: 31, label: 'TODAY — the five considerations' },
      { deck: ROOF, n: 33, label: 'TODAY — the five considerations' } ] },
];
function slidesForStep(title) {
  const t = String(title || '').toLowerCase();
  const hit = STEP_SLIDES.find(x => t.includes(x.match));
  return hit ? hit.slides : [];
}
function embedUrl(url) {
  if (!url) return null;
  let m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
  if (m) return `https://www.youtube.com/embed/${m[1]}`;
  m = url.match(/drive\.google\.com\/file\/d\/([^/]+)/);
  if (m) return `https://drive.google.com/file/d/${m[1]}/preview`;
  m = url.match(/drive\.google\.com\/open\?id=([\w-]+)/);
  if (m) return `https://drive.google.com/file/d/${m[1]}/preview`;
  return null;
}

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,600;9..144,700&family=Montserrat:wght@500;600;700;800&display=swap');
.cu{--ink:#12281F;--dim:#6D8177;--line:#E2E9E5;--gold:#C9922A;--gold2:#E0B24A;--green:#0D3B2E;--paper:#fff;
  font-family:Montserrat,system-ui,sans-serif;color:var(--ink);max-width:820px;padding-bottom:56px}
.cu-eb{font-size:10.5px;font-weight:800;letter-spacing:.2em;text-transform:uppercase;color:var(--gold);margin-bottom:7px}
.cu-h{font-family:Fraunces,Georgia,serif;font-size:clamp(26px,3.4vw,33px);font-weight:700;letter-spacing:-.022em;margin:0;line-height:1.05}
.cu-l{font-size:13.5px;color:var(--dim);margin-top:7px}
.cu-back{background:none;border:none;font:inherit;font-size:12.5px;font-weight:700;color:var(--dim);cursor:pointer;
  padding:0;margin-bottom:14px;display:inline-flex;gap:7px;align-items:center}
.cu-back:hover{color:var(--ink)}

/* course row */
.cu-course{display:flex;gap:20px;align-items:center;background:var(--paper);border:1px solid var(--line);
  border-radius:20px;padding:22px 24px;margin-bottom:13px;cursor:pointer;width:100%;text-align:left;font:inherit;
  box-shadow:0 1px 2px rgba(18,40,31,.05);transition:border-color .2s,transform .22s cubic-bezier(.2,.8,.3,1),box-shadow .22s}
.cu-course:hover{border-color:var(--gold);transform:translateY(-2px);box-shadow:0 12px 26px -16px rgba(18,40,31,.35)}
.cu-course:focus-visible{outline:2px solid var(--gold);outline-offset:3px}
.cu-ring{width:60px;height:60px;flex:0 0 auto;position:relative}
.cu-ring svg{transform:rotate(-90deg)}
.cu-ring b{position:absolute;inset:0;display:grid;place-items:center;font-size:13px;font-weight:800}
.cu-ct{flex:1;min-width:0}
.cu-ct h2{font-family:Fraunces,Georgia,serif;font-size:20px;margin:0 0 4px;font-weight:700;letter-spacing:-.012em}
.cu-ct p{margin:0;font-size:12.5px;color:var(--dim)}
.cu-nx{margin-top:8px;font-size:12.5px;font-weight:700}
.cu-nx span{color:var(--dim);font-weight:600}
.cu-go{background:var(--green);color:#fff;border:none;border-radius:999px;padding:12px 22px;font:inherit;
  font-weight:800;font-size:13.5px;white-space:nowrap;flex:0 0 auto}

/* up-next hero */
.cu-hero{background:linear-gradient(135deg,#14503C,#0D3B2E);border-radius:20px;padding:22px 24px;color:#fff;
  display:flex;align-items:center;gap:20px;margin-bottom:18px;position:relative;overflow:hidden}
.cu-hero .k{font-size:10px;font-weight:800;letter-spacing:.16em;color:var(--gold2);margin-bottom:7px}
.cu-hero h2{font-family:Fraunces,Georgia,serif;font-size:22px;margin:0 0 5px;font-weight:700;letter-spacing:-.014em}
.cu-hero p{margin:0;font-size:12.5px;opacity:.86;max-width:44ch}
.cu-hbtn{background:var(--gold2);color:#20321A;border:none;border-radius:999px;padding:14px 24px;font:inherit;
  font-weight:800;font-size:14.5px;cursor:pointer;white-space:nowrap;flex:0 0 auto;
  box-shadow:0 6px 18px -7px rgba(224,178,74,.8);transition:transform .2s,filter .2s}
.cu-hbtn:hover{transform:translateY(-1px);filter:brightness(1.06)}
.cu-bar{height:6px;background:#E3EAE6;border-radius:99px;overflow:hidden;margin-bottom:6px}
.cu-bar i{display:block;height:100%;background:var(--gold);border-radius:99px;transition:width 1s cubic-bezier(.2,.8,.3,1)}
.cu-barl{font-size:12px;color:var(--dim);font-weight:600;margin-bottom:20px}

/* step list */
.cu-steps{background:var(--paper);border:1px solid var(--line);border-radius:18px;overflow:hidden}
.cu-s{display:flex;align-items:center;gap:15px;padding:15px 19px;border-bottom:1px solid #F1F5F3;width:100%;
  text-align:left;font:inherit;background:none;border-left:none;border-right:none;border-top:none;cursor:pointer;
  transition:background .16s}
.cu-s:last-child{border-bottom:none}
.cu-s:hover:not(:disabled){background:#F8FBF9}
.cu-s:disabled{opacity:.45;cursor:not-allowed}
.cu-s:focus-visible{outline:2px solid var(--gold);outline-offset:-2px}
.cu-k{width:30px;height:30px;border-radius:50%;display:grid;place-items:center;font-size:12.5px;font-weight:800;
  background:#EEF3F0;color:#7B8D84;flex:0 0 auto}
.cu-k.d{background:#F6EAD1;color:#B08419}
.cu-k.n{background:var(--green);color:#fff}
.cu-st{flex:1;min-width:0}
.cu-sn{font-size:14.5px;font-weight:700;display:block}
.cu-sd{font-size:12px;color:var(--dim);margin-top:2px;display:block}
.cu-sm{font-size:11.5px;color:#93A29B;white-space:nowrap;font-weight:600}

/* open step */
.cu-slides{margin-top:14px;border-top:1px solid #EEF2F0;padding-top:13px}
.cu-slides h4{font-size:11.5px;font-weight:800;letter-spacing:.09em;text-transform:uppercase;
  color:var(--dim);margin:0 0 4px}
.cu-slides p{font-size:12px;color:var(--dim);margin:0 0 10px}
.cu-stage{position:relative;display:block;width:100%;padding:0;border-radius:14px;overflow:hidden;
  background:#0B2018;aspect-ratio:16/9;border:1px solid #D9E2DD;cursor:zoom-in;
  box-shadow:0 2px 10px rgba(15,36,29,.14);transition:box-shadow .22s}
.cu-stage:hover{box-shadow:0 12px 28px -14px rgba(15,36,29,.5)}
.cu-stage:focus-visible{outline:3px solid var(--gold);outline-offset:3px}
.cu-stage img{width:100%;height:100%;object-fit:cover;display:block}
.cu-thumbs{display:flex;gap:8px;overflow-x:auto;padding:2px 0 4px;scrollbar-width:thin}
.cu-thumb{position:relative;flex:0 0 auto;width:104px;aspect-ratio:16/9;border-radius:9px;overflow:hidden;
  border:2px solid transparent;background:#0B2018;cursor:pointer;padding:0;opacity:.62;
  transition:opacity .18s,border-color .18s,transform .18s}
.cu-thumb img{width:100%;height:100%;object-fit:cover;display:block}
.cu-thumb:hover{opacity:1;transform:translateY(-2px)}
.cu-thumb.on{opacity:1;border-color:var(--gold)}
.cu-thumb:focus-visible{outline:2px solid var(--gold);outline-offset:2px}
.cu-tnum{position:absolute;left:4px;top:4px;background:rgba(10,26,20,.78);color:#fff;font-size:9.5px;
  font-weight:800;padding:2px 6px;border-radius:999px}
.cu-scaption{font-size:12.5px;color:var(--dim);margin:9px 0 11px;font-weight:600;display:flex;
  align-items:center;gap:9px;flex-wrap:wrap}
.cu-scount{background:#EEF3F0;color:#4A5D55;font-size:10.5px;font-weight:800;padding:3px 9px;border-radius:999px}
.cu-schips{display:flex;gap:7px;flex-wrap:wrap}
.cu-chip{font:inherit;font-size:12px;font-weight:700;cursor:pointer;border-radius:999px;padding:8px 13px;
  border:1px solid #DDE5E1;background:#fff;color:#4A5D55;transition:all .16s;max-width:100%;
  overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.cu-chip:hover{border-color:var(--green);color:var(--ink)}
.cu-chip.on{background:var(--green);border-color:var(--green);color:#fff}
.cu-chip.on.coat{background:#8A6B1F;border-color:#8A6B1F}
.cu-chip:focus-visible{outline:2px solid var(--gold);outline-offset:2px}
.cu-full{background:none;border:none;font:inherit;font-size:12px;font-weight:700;color:var(--green);
  cursor:pointer;text-decoration:underline;padding:0}
.cu-open{margin:0;background:#FBFCFB;border-top:2px solid var(--gold);border-bottom:1px solid var(--line);
  overflow:hidden;scroll-margin-top:80px}
.cu-oh{padding:15px 20px;background:linear-gradient(135deg,#0D3B2E,#16543c);color:#fff}
.cu-oh b{font-size:16.5px;font-weight:700;display:block}
.cu-oh span{font-size:12px;opacity:.85}
.cu-ob{padding:18px}
.cu-task{display:flex;align-items:center;gap:15px;padding:17px 18px;border-radius:14px;cursor:pointer;
  margin-bottom:10px;background:#fff;border:1px solid var(--line);width:100%;text-align:left;font:inherit;
  transition:border-color .2s,transform .2s cubic-bezier(.2,.8,.3,1),box-shadow .2s}
.cu-task:hover:not(:disabled){border-color:#C6D3CD;transform:translateY(-2px);box-shadow:0 10px 22px -14px rgba(18,40,31,.4)}
/* what is finished steps back; what is next comes forward */
.cu-task.done{background:#F6F9F7;border-color:#EAEFEC}
.cu-task.done .cu-tt{color:#5E6E66;font-weight:600}
.cu-task.now{border-color:#DCC58A;box-shadow:0 3px 14px -6px rgba(201,146,42,.45)}
.cu-task:disabled{opacity:.5;cursor:not-allowed}
.cu-task:focus-visible{outline:3px solid var(--gold);outline-offset:2px}
.cu-num{width:34px;height:34px;flex:0 0 auto;border-radius:50%;display:grid;place-items:center;
  font-size:15px;font-weight:800;background:#EEF3F0;color:#6E7C76}
.cu-num.done{background:#E4F3EC;color:#1B7A5A}
.cu-tt{display:block;font-size:16px;font-weight:700;letter-spacing:-.005em}
.cu-ts{display:block;font-size:12.5px;color:var(--dim);margin-top:3px}
.cu-ts.warn{color:#B4453A;font-weight:700}
.cu-go2{flex:0 0 auto;display:inline-flex;align-items:center;gap:7px;background:var(--green);color:#fff;
  border-radius:999px;padding:10px 18px;font-size:13.5px;font-weight:800;white-space:nowrap}
.cu-go2.gold{background:linear-gradient(135deg,#E0B24A,#C9922A);color:#20321A}
.cu-go2.quiet{background:#EEF3F0;color:#4A5D55}
@media(max-width:600px){.cu-task{flex-wrap:wrap}.cu-go2{width:100%;justify-content:center;padding:12px 18px}}
.cu-cheat{font-size:10px;background:#FFF3CD;color:#8A6D00;padding:3px 9px;border-radius:10px;font-weight:700}
.cu-quiz{margin-top:6px;padding:14px 15px;border-radius:12px}
.cu-guides{background:var(--paper);border:1px solid var(--line);border-radius:18px;overflow:hidden}
@media(max-width:680px){.cu-course{flex-wrap:wrap}.cu-go{width:100%}
  .cu-hero{flex-wrap:wrap}.cu-hbtn{width:100%}}
.cu-guide{display:flex;gap:17px;align-items:center;background:none;border:none;border-bottom:1px solid #EDF2EF;
  padding:20px 20px;cursor:pointer;width:100%;text-align:left;font:inherit;
  transition:background .16s,transform .16s}
.cu-guide:last-child{border-bottom:none}
.cu-guide:hover{background:#F5F9F7}
.cu-guide:active{transform:scale(.995);background:#EEF4F0}
.cu-guide:focus-visible{outline:3px solid var(--gold);outline-offset:-3px}
.cu-gico{width:52px;height:52px;flex:0 0 auto;border-radius:15px;display:grid;place-items:center;font-size:25px;background:#EEF4F0}
.cu-guide.tool .cu-gico{background:#FBF3E2}
.cu-guide b{display:block;font-size:17px;font-weight:800;letter-spacing:-.01em;line-height:1.28}
.cu-guide s{display:block;font-size:13.5px;color:var(--dim);text-decoration:none;margin-top:3px;line-height:1.4}
/* an explicit button, so it reads as tappable rather than as a list item */
.cu-gbtn{flex:0 0 auto;box-sizing:border-box;display:inline-flex;align-items:center;gap:7px;background:var(--green);color:#fff;
  border-radius:999px;padding:11px 20px;font-size:14px;font-weight:800;white-space:nowrap;
  box-shadow:0 2px 8px -3px rgba(13,59,46,.5);transition:transform .18s,filter .18s}
.cu-guide.tool .cu-gbtn{background:linear-gradient(135deg,#E0B24A,#C9922A);color:#20321A}
.cu-guide:hover .cu-gbtn{transform:translateY(-1px);filter:brightness(1.07)}
@media(max-width:600px){
  .cu-guide{flex-wrap:wrap;gap:13px;padding:18px 16px}
  .cu-gico{width:46px;height:46px;font-size:22px}
  .cu-guide b{font-size:16px}
  .cu-gbtn{flex:1 0 100%;width:auto;justify-content:center;padding:14px 20px;font-size:15px;margin-top:2px}
}
.cu-tag{font-size:9.5px;font-weight:800;letter-spacing:.05em;color:var(--gold);border:1px solid rgba(201,146,42,.4);
  border-radius:999px;padding:3px 8px;white-space:nowrap}
.cu-sec{font-family:Fraunces,Georgia,serif;font-size:17px;font-weight:700;margin:30px 0 3px}
.cu-secs{font-size:12.5px;color:var(--dim);margin-bottom:13px}
.cu-tabs{display:grid;grid-template-columns:repeat(3,1fr);gap:11px;margin-bottom:24px}
.cu-tab{font:inherit;cursor:pointer;border:2px solid var(--line);background:var(--paper);color:var(--dim);
  border-radius:16px;padding:16px 14px;display:flex;flex-direction:column;align-items:center;gap:7px;
  transition:border-color .2s,background .2s,color .2s,transform .2s cubic-bezier(.2,.8,.3,1),box-shadow .2s;
  box-shadow:0 1px 2px rgba(18,40,31,.05)}
.cu-tab:hover{border-color:#C3D2CB;transform:translateY(-2px);box-shadow:0 10px 22px -14px rgba(18,40,31,.4)}
.cu-tab:active{transform:translateY(0)}
.cu-tab:focus-visible{outline:3px solid var(--gold);outline-offset:3px}
.cu-tab .ic{font-size:26px;line-height:1}
.cu-tab .lb{font-size:15.5px;font-weight:800;letter-spacing:-.01em;color:var(--ink)}
.cu-tab .hint{font-size:11.5px;font-weight:600;color:var(--dim);text-align:center;line-height:1.3}
.cu-tab.on{background:var(--green);border-color:var(--green);box-shadow:0 8px 20px -10px rgba(13,59,46,.6)}
.cu-tab.on .lb{color:#fff}
.cu-tab.on .hint{color:rgba(255,255,255,.8)}
@media(max-width:620px){.cu-tabs{gap:8px}.cu-tab{padding:13px 8px;border-radius:14px}
  .cu-tab .ic{font-size:22px}.cu-tab .lb{font-size:13px}.cu-tab .hint{display:none}}
.cu-vsec{margin-bottom:30px}
.cu-vh2{font-family:Fraunces,Georgia,serif;font-size:19px;font-weight:700;margin:0 0 3px;letter-spacing:-.012em}
.cu-vsub{font-size:12.5px;color:var(--dim);margin-bottom:13px}
.cu-vgrid{display:grid;grid-template-columns:repeat(auto-fill,minmax(230px,1fr));gap:14px}
.cu-vcard{position:relative;border:none;padding:0;background:none;font:inherit;text-align:left;cursor:pointer;
  border-radius:15px;overflow:hidden;background:var(--paper);border:1px solid var(--line);
  box-shadow:0 1px 2px rgba(18,40,31,.05);transition:transform .24s cubic-bezier(.2,.8,.3,1),box-shadow .24s,border-color .24s}
.cu-vcard:hover{transform:translateY(-3px);border-color:#CBD8D2;box-shadow:0 14px 28px -16px rgba(18,40,31,.4)}
.cu-vcard:focus-visible{outline:3px solid var(--gold);outline-offset:3px}
.cu-vthumb{position:relative;aspect-ratio:16/9;background:#12281F;overflow:hidden;display:block}
.cu-vthumb video,.cu-vthumb img{width:100%;height:100%;object-fit:cover;display:block}
.cu-vplay{position:absolute;inset:0;display:grid;place-items:center;background:rgba(10,26,20,.28);transition:background .24s}
.cu-vcard:hover .cu-vplay{background:rgba(10,26,20,.12)}
.cu-vplay span{width:50px;height:50px;border-radius:50%;background:rgba(255,255,255,.94);color:#0D3B2E;
  display:grid;place-items:center;font-size:17px;padding-left:3px;box-shadow:0 4px 14px rgba(0,0,0,.3);
  transition:transform .24s cubic-bezier(.3,1.4,.5,1)}
.cu-vcard:hover .cu-vplay span{transform:scale(1.12)}
.cu-vname{padding:12px 14px 14px;font-size:14px;font-weight:700;line-height:1.32}
.cu-vnum{position:absolute;top:9px;left:10px;background:rgba(10,26,20,.72);color:#fff;font-size:10.5px;
  font-weight:800;padding:3px 8px;border-radius:999px;backdrop-filter:blur(4px)}
.cu-player{position:fixed;inset:0;background:rgba(6,15,12,.95);z-index:9999;display:flex;align-items:center;
  justify-content:center;padding:22px;flex-direction:column;gap:14px}
.cu-player video,.cu-player iframe{max-width:min(1100px,94vw);max-height:78vh;width:100%;border-radius:10px;background:#000;border:0}
.cu-ptitle{color:#fff;font-size:15px;font-weight:700;text-align:center;max-width:70ch}
.cu-pclose{position:absolute;top:18px;right:20px;width:44px;height:44px;border-radius:50%;border:none;
  background:rgba(255,255,255,.16);color:#fff;font-size:18px;cursor:pointer}
.cu-pclose:hover{background:rgba(255,255,255,.3)}
.cu-vid{background:var(--paper);border:1px solid var(--line);border-radius:15px;overflow:hidden;margin-bottom:10px}
.cu-vh{display:flex;gap:13px;align-items:center;padding:15px 18px;width:100%;font:inherit;background:none;border:none;cursor:pointer;text-align:left}
.cu-vb{padding:0 18px 18px;display:flex;flex-direction:column;gap:15px}
.cu-vb video{width:100%;border-radius:11px;background:#000;max-height:380px}
.cu-empty{border:1px dashed #D5DEDA;border-radius:16px;padding:32px;text-align:center;color:var(--dim);font-size:13.5px}
`;

/* The slides that belong to a step, shown as images right here on the page.
   One large view plus a thumbnail strip; nothing loads from another page. */
function StepSlides({ slides, onExpand }) {
  const [i, setI] = useState(0);
  const cur = slides[i] || slides[0];
  if (!cur) return null;
  return (
    <div className="cu-slides">
      <h4>Slides for this step</h4>
      <p>What the homeowner sees while you deliver this part.</p>

      <button className="cu-stage" onClick={() => onExpand(cur)} title="Tap to enlarge">
        <img src={SLIDE_IMG(cur.deck, cur.n)} alt={cur.label} />
      </button>

      <div className="cu-scaption">
        <span className="cu-scount">{cur.deck === COAT ? 'COATING' : 'ROOFING'} · SLIDE {cur.n}</span>
        <span style={{ flex: 1, minWidth: 0 }}>{cur.label}</span>
        <span style={{ color: '#9AA69F', fontSize: 11.5 }}>{i + 1} of {slides.length}</span>
      </div>

      {slides.length > 1 && (
        <div className="cu-thumbs">
          {slides.map((sl, k) => (
            <button key={k} className={'cu-thumb' + (k === i ? ' on' : '')}
              onClick={() => setI(k)} title={sl.label}>
              <img src={SLIDE_IMG(sl.deck, sl.n)} alt="" loading="lazy" />
              <span className="cu-tnum">{k + 1}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Training() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [levels, setLevels] = useState([]);
  const [videoSections, setVideoSections] = useState([]);
  const [loading, setLoading] = useState(true);
  /* Tab, course, open step and open guide all live in the URL, so every view
     can be bookmarked and shared, and the browser Back button works. */
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') || 'training';
  const courseParam = params.get('course');
  const expanded = params.get('step') ? parseInt(params.get('step'), 10) : null;
  const guideId = params.get('open');
  const [directGuide, setDirectGuide] = useState(null);
  const guide = directGuide || GUIDES.find(g => g.id === guideId) || null;

  const setParam = (patch, replace = false) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === null || v === undefined || v === '') next.delete(k);
      else next.set(k, String(v));
    });
    setParams(next, { replace });
  };
  const setTab = (t) => setParam({ tab: t === 'training' ? null : t, step: null, open: null });
  const setGuide = (g) => {
    if (g && g.id === 'slide') {          // a specific deck slide, not a catalogue guide
      logTrainingActivity('guide', 'slide', g.title);
      setDirectGuide(g);
      return;
    }
    setDirectGuide(null);
    if (g) logTrainingActivity('guide', g.id, g.title);
    setParam({ open: g ? g.id : null });
  };
  const [courseIdState, setCourseIdState] = useState(null);
  const [playing, setPlaying] = useState(null);
  const [bigSlide, setBigSlide] = useState(null);
  const openSection = params.get('sec') ? parseInt(params.get('sec'), 10) : null;
  const setOpenSection = (id) => setParam({ sec: id });


  useEffect(() => {
    function onMsg(e) { if (e.data === 'close-vibrant') setParam({ open: null }); }
    function onEsc(e) { if (e.key === 'Escape') { setBigSlide(null); setDirectGuide(null); setParam({ open: null, video: null }); setPlaying(null); } }
    window.addEventListener('message', onMsg);
    document.addEventListener('keydown', onEsc);
    return () => { window.removeEventListener('message', onMsg); document.removeEventListener('keydown', onEsc); };
  }, [params]);
  useEffect(() => { load(); }, []);

  // Tests an admin has unlocked for me. Checked every 15 seconds and whenever the page gets focus,
  // so a test unlocked by the manager shows up on its own, no refresh needed.
  const [myTests, setMyTests] = useState([]);
  useEffect(() => {
    let live = true;
    const check = () => getMyTests().then(r => { if (live) setMyTests(r.tests || []); }).catch(() => {});
    check();
    const t = setInterval(check, 15000);
    window.addEventListener('focus', check);
    return () => { live = false; clearInterval(t); window.removeEventListener('focus', check); };
  }, []);
  const openTests = myTests.filter(t => t.status === 'open' || t.status === 'retry').length;

  async function load() {
    setLoading(true);
    try {
      const [d, v] = await Promise.all([getCurriculum(), getVideoLibrary().catch(() => ({ sections: [] }))]);
      const cs = d.courses || [];
      setCourses(cs);
      setLevels(d.levels || []);
      setVideoSections(v.sections || []);
      // With a single course there is nothing to choose — go straight in.
      if (cs.length === 1) setCourseIdState(cs[0].id);
    } catch { toast.error('Could not load CPR University'); }
    finally { setLoading(false); }
  }

  const courseId = courseParam ? parseInt(courseParam, 10) : courseIdState;
  const setCourseId = (id) => { setCourseIdState(id); setParam({ course: id, step: null }); };
  const course = courses.find(c => c.id === courseId) || null;
  const steps = courseId ? levels.filter(l => l.course_id === courseId) : levels;
  const next = steps.find(l => !l.locked && !l.complete);
  const done = steps.filter(l => l.complete).length;
  const pct = steps.length ? Math.round(done / steps.length * 100) : 0;

  function openStep(lvl) {
    if (lvl.locked) return;
    setParam({ step: expanded === lvl.id ? null : lvl.id });
  }
  function goToNext() {
    if (!next) return;
    setParam({ step: next.id });
    setTimeout(() => document.getElementById('cu-open')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
  }

  // YouTube gives us a free thumbnail; uploaded files use their own first frame.
  function ytThumb(url) {
    if (!url) return null;
    let m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
    if (m) return `https://img.youtube.com/vi/${m[1]}/hqdefault.jpg`;
    // Google Drive serves a still for any file the link-holder can view
    m = url.match(/drive\.google\.com\/file\/d\/([^/]+)/) || url.match(/drive\.google\.com\/open\?id=([\w-]+)/);
    if (m) return `https://drive.google.com/thumbnail?id=${m[1]}&sz=w480`;
    return null;
  }
  function playVideo(v, sectionTitle) {
    logTrainingActivity('video', v.id, v.title, 0);
    setParam({ video: v.id });
    setPlaying({ ...v, section: sectionTitle });
  }


  /* The open step, rendered directly under the row that was tapped rather than
     at the bottom of the page — otherwise tapping step 1 shows its content
     below step 13. */
  function OpenStep({ lvl }) {
    if (!lvl || lvl.locked) return null;
    return (
                  <div className="cu-open" id="cu-open">

                    <div className="cu-ob">
                      {lvl.lessons.map(les => (
                        <button key={les.id} className={'cu-task' + (les.completed ? ' done' : ' now')}
                          onClick={() => navigate(`/training/lesson/${les.id}`)}>
                          <span className={'cu-num' + (les.completed ? ' done' : '')}>{les.completed ? '✓' : '1'}</span>
                          <span style={{ flex: 1, minWidth: 0 }}>
                            <span className="cu-tt">Read the lesson</span>
                            <span className="cu-ts">
                              {les.completed ? 'Finished' : 'The script, word for word'}
                              {les.has_cheatsheet ? ' · includes a cheat-sheet' : ''}
                            </span>
                          </span>
                          <span className="cu-go2">{les.completed ? 'Read again' : 'Open'} <span aria-hidden>→</span></span>
                        </button>
                      ))}
                      {/* Slides live inside the lesson itself, not on this list.
                          Flip SHOW_STEP_SLIDES to true to bring them back here. */}
                      {SHOW_STEP_SLIDES && slidesForStep(lvl.title).length > 0 && (
                        <StepSlides slides={slidesForStep(lvl.title)} onExpand={setBigSlide} />
                      )}

                      {lvl.quiz ? (
                        <button className={'cu-task' + (lvl.quiz.passed ? ' done' : ' now')}
                          disabled={lvl.quiz.attempts_left === 0 && !lvl.quiz.passed}
                          onClick={() => navigate(`/training/quiz/${lvl.quiz.id}`)}>
                          <span className={'cu-num' + (lvl.quiz.passed ? ' done' : '')}>{lvl.quiz.passed ? '✓' : '2'}</span>
                          <span style={{ flex: 1, minWidth: 0 }}>
                            <span className="cu-tt">Take the quiz</span>
                            <span className={'cu-ts' + (!lvl.quiz.passed && lvl.quiz.attempts_left <= 1 ? ' warn' : '')}>
                              {lvl.quiz.passed
                                ? `Passed with ${lvl.quiz.best_score}%`
                                : `${lvl.passing_score}% to pass · ${lvl.quiz.attempts_left} ${lvl.quiz.attempts_left === 1 ? 'try' : 'tries'} left`}
                            </span>
                          </span>
                          <span className={'cu-go2' + (lvl.quiz.passed ? ' quiet' : ' gold')}>
                            {lvl.quiz.passed ? 'Retake' : lvl.quiz.attempts_left === 0 ? 'No tries left' : 'Start'} <span aria-hidden>→</span>
                          </span>
                        </button>
                      ) : (
                        <div style={{ marginTop: 8, fontSize: 12, color: '#9AA69F', fontStyle: 'italic' }}>
                          No quiz on this step yet — finishing the lesson completes it.
                        </div>
                      )}
                    </div>
                  </div>
                );
  }

  const Ring = ({ done: d, total }) => {
    const R = 25, C = 2 * Math.PI * R, p = total ? d / total : 0;
    return (
      <span className="cu-ring">
        <svg width="60" height="60"><circle cx="30" cy="30" r={R} fill="none" stroke="#E6ECE9" strokeWidth="6" />
          <circle cx="30" cy="30" r={R} fill="none" stroke="#C9922A" strokeWidth="6" strokeLinecap="round"
            strokeDasharray={C} strokeDashoffset={C * (1 - p)} /></svg>
        <b>{d}/{total}</b>
      </span>
    );
  };

  if (loading) return (<>
    <div className="page-header"><h2>🎓 CPR University</h2></div>
    <div className="page-body"><div style={{ padding: 30, color: '#aaa' }}>Loading…</div></div>
  </>);

  return (
    <>
      <style>{CSS}</style>
      {playing && (
        <div className="cu-player" onClick={e => { if (e.target === e.currentTarget) { setPlaying(null); setParam({ video: null }); } }}>
          {playing.filename
            ? <video src={getTrainingVideoUrl(playing.filename)} controls autoPlay
                onPause={e => logTrainingActivity('video', playing.id, playing.title, Math.round(e.currentTarget.currentTime))}
                onEnded={e => logTrainingActivity('video', playing.id, playing.title, Math.round(e.currentTarget.duration || 0))} />
            : embedUrl(playing.url)
              ? <iframe title={playing.title}
                  src={embedUrl(playing.url) + (embedUrl(playing.url).includes('youtube') ? '?autoplay=1' : '')}
                  allow="autoplay; encrypted-media; fullscreen" allowFullScreen style={{ aspectRatio: '16/9' }} />
              : <a className="btn btn-primary" href={playing.url} target="_blank" rel="noreferrer">Open video</a>}
          <div className="cu-ptitle">{playing.title}{playing.section ? ` · ${playing.section}` : ''}</div>
          <button className="cu-pclose" aria-label="Close"
            onClick={() => { setPlaying(null); setParam({ video: null }); }}>✕</button>
        </div>
      )}

      {bigSlide && (
        <div className="cu-player" onClick={e => { if (e.target === e.currentTarget) setBigSlide(null); }}>
          <img src={SLIDE_IMG(bigSlide.deck, bigSlide.n)} alt={bigSlide.label}
            style={{ maxWidth: 'min(1400px,96vw)', maxHeight: '84vh', borderRadius: 10 }} />
          <div className="cu-ptitle">{bigSlide.label}</div>
          <button className="cu-pclose" aria-label="Close" onClick={() => setBigSlide(null)}>✕</button>
        </div>
      )}

      {guide && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: '#000' }}>
          <iframe title={guide.title} src={guide.url} style={{ width: '100%', height: '100%', border: 0 }} allow="autoplay; fullscreen" />
          <button onClick={() => setGuide(null)} aria-label="Close"
            style={{ position: 'absolute', top: 14, right: 18, width: 42, height: 42, borderRadius: '50%', border: 0,
              background: 'rgba(255,255,255,.16)', color: '#fff', fontSize: 18, cursor: 'pointer', zIndex: 2 }}>✕</button>
        </div>
      )}

      <div className="page-header"><h2>🎓 CPR University</h2></div>

      <div className="page-body cu">
        {openTests > 0 && tab !== 'tests' && (
          <button type="button" onClick={() => setTab('tests')} style={{ width: '100%', textAlign: 'left', border: 0, cursor: 'pointer', marginBottom: 14, borderRadius: 14, padding: '14px 18px',
            background: 'linear-gradient(135deg,#1f5b3a,#2d7a52)', color: '#fff', fontWeight: 800, fontSize: 15, boxShadow: '0 10px 22px -14px rgba(13,59,46,.7)' }}>
            📝 {openTests === 1 ? 'A test has been unlocked for you.' : `${openTests} tests have been unlocked for you.`} <span style={{ textDecoration: 'underline', marginLeft: 6 }}>Open Tests →</span>
          </button>
        )}
        <div className="cu-tabs" style={{ gridTemplateColumns: `repeat(${2 + (videoSections.length ? 1 : 0) + (myTests.length ? 1 : 0)},1fr)` }}>
          <button className={'cu-tab' + (tab === 'training' ? ' on' : '')} onClick={() => setTab('training')}>
            <span className="ic" aria-hidden>🎓</span>
            <span className="lb">My Training</span>
            <span className="hint">Your course and quizzes</span>
          </button>
          <button className={'cu-tab' + (tab === 'lookup' ? ' on' : '')} onClick={() => setTab('lookup')}>
            <span className="ic" aria-hidden>🔍</span>
            <span className="lb">Quick Answers</span>
            <span className="hint">Look something up fast</span>
          </button>
          {videoSections.length > 0 && (
            <button className={'cu-tab' + (tab === 'videos' ? ' on' : '')} onClick={() => setTab('videos')}>
              <span className="ic" aria-hidden>🎬</span>
              <span className="lb">Videos</span>
              <span className="hint">Watch training recordings</span>
            </button>
          )}
          {myTests.length > 0 && (
            <button className={'cu-tab' + (tab === 'tests' ? ' on' : '')} onClick={() => setTab('tests')}>
              <span className="ic" aria-hidden>📝</span>
              <span className="lb">Tests{openTests ? ` (${openTests})` : ''}</span>
              <span className="hint">Unlocked by your manager</span>
            </button>
          )}
        </div>

        {/* ── TESTS (only what an admin has unlocked) ── */}
        {tab === 'tests' && (<>
          <div className="cu-eb">Tests</div>
          <h1 className="cu-h">Your tests</h1>
          <div className="cu-l">Unlocked by your manager after class.</div>
          {myTests.length ? <MyTests tests={myTests} /> : <div className="cu-empty" style={{ marginTop: 20 }}>No tests are unlocked for you right now.</div>}
        </>)}

        {/* ── TRAINING ── */}
        {tab === 'training' && (<>
          {/* choose a course, only when there is a choice to make */}
          {!courseId && courses.length > 1 && (<>
            <div className="cu-eb">CPR University</div>
            <h1 className="cu-h">My training</h1>
            <div className="cu-l">Pick a course to carry on where you left off.</div>
            <div style={{ marginTop: 22 }}>
              {courses.map(c => {
                const mine = levels.filter(l => l.course_id === c.id);
                const cDone = mine.filter(l => l.complete).length;
                const cNext = mine.find(l => !l.locked && !l.complete);
                return (
                  <button key={c.id} className="cu-course" onClick={() => setCourseId(c.id)}>
                    <Ring done={cDone} total={mine.length} />
                    <span className="cu-ct">
                      <h2>{c.title}</h2>
                      {c.description && <p>{c.description}</p>}
                      <span className="cu-nx" style={{ display: 'block' }}>
                        {cNext ? <>Up next: {cNext.title} <span>· {mine.length} steps</span></>
                          : mine.length ? <>All done <span>· {mine.length} steps</span></>
                          : <span>No steps yet</span>}
                      </span>
                    </span>
                    <span className="cu-go">{cDone ? 'Continue' : 'Start'} →</span>
                  </button>
                );
              })}
            </div>
          </>)}

          {/* inside a course */}
          {(courseId || courses.length <= 1) && (<>
            {courses.length > 1 && (
              <button className="cu-back" onClick={() => { setCourseIdState(null); setParam({ course: null, step: null }); }}>← All courses</button>
            )}
            <div className="cu-eb">{course ? 'Course' : 'CPR University'}</div>
            <h1 className="cu-h">{course ? course.title : 'Your training'}</h1>
            <div className="cu-l">{course?.description || 'Work through each step in order — read the lesson, pass the quiz, move on.'}</div>

            <div style={{ marginTop: 20 }}>
              {next ? (
                <div className="cu-hero">
                  <RepMascot height={96} style={{ flex: '0 0 auto' }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="k">UP NEXT · STEP {steps.indexOf(next) + 1} OF {steps.length}</div>
                    <h2>{next.title}</h2>
                    <p>{next.description || 'Read the lesson, then pass the quiz to open the next step.'}</p>
                  </div>
                  <button className="cu-hbtn" onClick={goToNext}>{done === 0 ? 'Start' : 'Continue'} →</button>
                </div>
              ) : steps.length > 0 ? (
                <div className="cu-hero">
                  <RepMascot height={96} style={{ flex: '0 0 auto' }} />
                  <div style={{ flex: 1 }}>
                    <div className="k">ALL DONE</div>
                    <h2>You passed every step</h2>
                    <p>Go back over any step below to keep it sharp.</p>
                  </div>
                </div>
              ) : null}

              {steps.length > 0 && (<>
                <div className="cu-bar"><i style={{ width: pct + '%' }} /></div>
                <div className="cu-barl">{done} of {steps.length} steps passed</div>

                <div className="cu-steps">
                  {steps.map((lvl, i) => {
                    const isNext = next && lvl.id === next.id;
                    return (
                      <div key={lvl.id}>
                      <button className="cu-s" disabled={lvl.locked} onClick={() => openStep(lvl)}>
                        <span className={'cu-k' + (lvl.complete ? ' d' : isNext ? ' n' : '')}>
                          {lvl.locked ? '🔒' : lvl.complete ? '✓' : i + 1}
                        </span>
                        <span className="cu-st">
                          <span className="cu-sn">{lvl.title}</span>
                          <span className="cu-sd">
                            {lvl.locked ? 'Pass the step before this one to unlock'
                              : lvl.complete ? 'Passed'
                              : isNext ? 'Up next' : 'Not started'}
                          </span>
                        </span>
                        <span className="cu-sm">
                          {lvl.complete && lvl.quiz?.best_score ? `quiz ${lvl.quiz.best_score}%`
                            : lvl.locked ? 'locked' : ''}
                        </span>
                      </button>
                      {expanded === lvl.id && <OpenStep lvl={lvl} />}
                      </div>
                    );
                  })}
                </div>
              </>)}

              {steps.length === 0 && <div className="cu-empty">No steps in this course yet.</div>}

              {/* the open step */}
            </div>
          </>)}
        </>)}

        {/* ── LOOK IT UP ── */}
        {tab === 'lookup' && (<>
          <div className="cu-eb">Quick answers</div>
          <h1 className="cu-h">Find an answer fast</h1>
          <div className="cu-l">No lessons, no quizzes — just answers. All of these work on your phone.</div>
          <div className="cu-sec">Standing at the house</div>
          <div className="cu-secs">Type what you are looking at and get the answer.</div>
          <div className="cu-guides">
            {GUIDES.filter(g => g.tag === 'Search').map(g => (
              <button key={g.id} className={'cu-guide' + (g.tag === 'Search' ? ' tool' : '')} onClick={() => setGuide(g)}>
                <span className="cu-gico" aria-hidden>{g.icon}</span>
                <span style={{ flex: 1, minWidth: 0 }}><b>{g.title}</b><s>{g.sub}</s></span>
                <span className="cu-gbtn">
                  {g.tag === 'Search' ? 'Search' : 'Watch'} <span aria-hidden>→</span>
                </span>
              </button>
            ))}
          </div>
          <div className="cu-sec">Learn before you go</div>
          <div className="cu-secs">Worth reading once, then you know it.</div>
          <div className="cu-guides">
            {GUIDES.filter(g => g.tag !== 'Search').map(g => (
              <button key={g.id} className={'cu-guide' + (g.tag === 'Search' ? ' tool' : '')} onClick={() => setGuide(g)}>
                <span className="cu-gico" aria-hidden>{g.icon}</span>
                <span style={{ flex: 1, minWidth: 0 }}><b>{g.title}</b><s>{g.sub}</s></span>
                <span className="cu-gbtn">
                  {g.tag === 'Search' ? 'Search' : 'Watch'} <span aria-hidden>→</span>
                </span>
              </button>
            ))}
          </div>
        </>)}

        {/* ── VIDEOS ── */}
        {tab === 'videos' && (<>
          <div className="cu-eb">Videos</div>
          <h1 className="cu-h">Training videos</h1>
          <div className="cu-l">Recordings from training sessions with the team.</div>
          <div style={{ marginTop: 22 }}>
            {videoSections.length === 0 && (
              <div className="cu-empty">No videos yet — an admin can add them from University Admin.</div>
            )}
            {videoSections.map(sec => (
              <div className="cu-vsec" key={sec.id}>
                <h2 className="cu-vh2">{sec.title}</h2>
                <div className="cu-vsub">
                  {sec.videos.length} video{sec.videos.length === 1 ? '' : 's'}
                </div>
                {sec.videos.length === 0
                  ? <div className="cu-empty" style={{ padding: 22 }}>Nothing in here yet.</div>
                  : (
                    <div className="cu-vgrid">
                      {sec.videos.map((v, i) => (
                        <button key={v.id} className="cu-vcard" onClick={() => playVideo(v, sec.title)}>
                          <span className="cu-vthumb">
                            {v.filename
                              ? <video src={getTrainingVideoUrl(v.filename) + '#t=2'} preload="metadata" muted playsInline />
                              : ytThumb(v.url)
                                ? <img src={ytThumb(v.url)} alt="" loading="lazy"
                                    onError={e => { e.currentTarget.style.display = 'none'; }} />
                                : <span style={{ display: 'grid', placeItems: 'center', height: '100%', fontSize: 30 }}>🎬</span>}
                            <span className="cu-vplay"><span aria-hidden>▶</span></span>
                            <span className="cu-vnum">{i + 1}</span>
                          </span>
                          <span className="cu-vname" style={{ display: 'block' }}>{v.title}</span>
                        </button>
                      ))}
                    </div>
                  )}
              </div>
            ))}
          </div>
        </>)}
      </div>
    </>
  );
}
